import { useTranslation } from 'next-i18next';
import React, { FC } from 'react';
import { scroller } from 'react-scroll';
import { ISubCategory } from 'routes/topics/topics';
import styled from 'styled-components';

import { SearchItem } from '../search-item';

interface SearchResultsProps {
  results: ISubCategory[];
  isShowPopularRubrics: boolean;
  defaultResults: ISubCategory[];
  searchString: string;
  setIsShowBackground: React.Dispatch<React.SetStateAction<boolean>>;
  setIsShowResults: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SearchResults: FC<SearchResultsProps> = ({
  results,
  isShowPopularRubrics,
  defaultResults,
  searchString,
  setIsShowBackground,
  setIsShowResults,
}) => {
  const { t } = useTranslation('utils');

  const onClickHandler = () => {
    setIsShowBackground(false);
    setIsShowResults(false);
    scroller.scrollTo('rubricsList', {
      duration: 2500,
      delay: 100,
      smooth: 'easeInOutQuint',
      offset: -90,
    });
  };

  return (
    <>
      {isShowPopularRubrics && (
        <PopularTopicsResults>
          <StyledTitleWrapper>
            <StyledTitle>{t('popularTopics')}</StyledTitle>
          </StyledTitleWrapper>
          {defaultResults.map((item) => (
            <SearchItem
              key={item.id}
              rubricName={item.name}
              rubricId={item.id}
              searchString={searchString}
              setIsShowBackground={setIsShowBackground}
              setIsShowResults={setIsShowResults}
            />
          ))}
        </PopularTopicsResults>
      )}
      {results.length === 0 && (
        <SearchResultsContainer>
          <NoResultsWrapper>
            <NoResultsTitleWrapper>
              <NoResultsTitle>{t('noTopics.title')}</NoResultsTitle>
            </NoResultsTitleWrapper>
            <NoResultsSubTitleWrapper>
              <NoResultsSubTitle>{t('noTopics.subTitle')}</NoResultsSubTitle>
              <RubricsListLinkWrapper onClick={onClickHandler}>{t('noTopics.catalog')}</RubricsListLinkWrapper>
            </NoResultsSubTitleWrapper>
          </NoResultsWrapper>
        </SearchResultsContainer>
      )}
      {results && results.length > 8 && searchString.trim() !== '' && (
        <SearchResultsContainer>
          <ResultsList>
            {results.map((item) => (
              <SearchItem
                key={item.id}
                rubricName={item.name}
                rubricId={item.id}
                searchString={searchString}
                setIsShowBackground={setIsShowBackground}
                setIsShowResults={setIsShowResults}
                mainCategory={item.mainCategory}
              />
            ))}
          </ResultsList>
        </SearchResultsContainer>
      )}
      {results && results.length > 0 && results.length <= 8 && searchString.trim() !== '' && (
        <PopularTopicsResults>
          <ResultsList>
            {results.map((item) => (
              <SearchItem
                key={item.id}
                rubricName={item.name}
                rubricId={item.id}
                searchString={searchString}
                setIsShowBackground={setIsShowBackground}
                setIsShowResults={setIsShowResults}
                mainCategory={item.mainCategory}
              />
            ))}
          </ResultsList>
        </PopularTopicsResults>
      )}
    </>
  );
};

const SearchResultsContainer = styled.div`
  width: 100%;
  background-color: var(--white);
  border-radius: 12px;
  box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.3);
  padding: 20px 0;
  max-height: 350px;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    margin-block: 20px;
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: var(--gray2);
    height: 140px;
    background-clip: padding-box;
    border-right: 1px solid var(--white);
  }

  @media (max-width: 400px) {
    box-shadow: none;
    padding: 10px 0;
  }
`;

const ResultsList = styled.div``;

const StyledTitleWrapper = styled.div`
  padding-left: 20px;
  margin-bottom: 10px;

  @media (max-width: 400px) {
    padding-left: 0;
  }
`;

const StyledTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: var(--gray);
`;

const NoResultsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 20px;
  align-items: flex-start;
  justify-content: flex-start;

  @media (max-width: 400px) {
    padding-left: 0;
  }
`;

const NoResultsTitleWrapper = styled.div``;

const NoResultsSubTitleWrapper = styled.div``;

const NoResultsTitle = styled.span`
  font-size: 17px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: 2.35;
  letter-spacing: normal;
  color: var(--black2);
`;

const NoResultsSubTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: var(--gray2);
`;

const RubricsListLinkWrapper = styled.span`
  font-size: 13px;
  font-weight: 500;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  color: var(--purple);
  cursor: pointer;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

const PopularTopicsResults = styled.div`
  width: 100%;
  background-color: var(--white);
  border-radius: 12px;
  box-shadow: 0 0 15px 0 rgba(0, 0, 0, 0.3);
  padding: 20px 0;
  max-height: 350px;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 0;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    margin-block: 20px;
    background-clip: padding-box;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: var(--gray2);
    height: 140px;
    background-clip: padding-box;
    border-right: 1px solid var(--white);
  }

  @media (max-width: 400px) {
    box-shadow: none;
    padding: 10px 0;
  }
`;
